import React, {
  useState, useEffect, useRef, useCallback,
} from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import styled from '@emotion/styled';
import Dropdown from 'rc-dropdown';
import PropTypes from 'prop-types';
import Menu, { Item as MenuItem } from 'rc-menu';
import IcomoonReact from 'icomoon-react';
import Text from '../../../../shared/components/Text/Text';
import { TransitionGlobal } from '../../../../shared/GlobalStyles';
import iconSet from '../../../../shared/images/teambuildr-selection.json';
import {
  setNonDateBasedProgramDays,
  setSelectedProgramDayNumber,
  setActiveModal,
} from '../../ducks/calendarActions';
import usePreSaveNavigationGuard from '../../hooks/usePreSaveNavigationGuard';

const Wrapper = styled('div')`
  display: flex;
  width: 100%;
  padding: 8px;
  padding-right: 12px;
  padding-bottom: ${(props) => (props.viewType === 'weekView' ? '8px' : '12px')};
  background: white;
  align-items: center;
  gap: 8px;
  overflow: hidden;
  min-height: 50px;

  /* Only apply max-width and fixed positioning for weekView */
  ${(props) => props.viewType === 'weekView'
    && `
    max-width: calc(100% - ${props.isPrimaryAnimatedSideBarOpen ? 384 : 0}px - 48px);
    position: fixed;
    bottom: 0;

    @media screen and (max-width: 1023px) {
      max-width: calc(100% - ${props.isPrimaryAnimatedSideBarOpen ? 300 : 0}px - 48px);
    }
  `}
`;

const WeeksWrapper = styled('div')`
  display: flex;
  gap: 8px;
  min-width: 0;
  overflow: hidden;
`;

const ArrowsWrapper = styled('div')`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const ArrowButton = styled('button')`
  border: none;
  background: transparent;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 28px;
  min-width: 28px;
  height: 28px;
  cursor: ${(props) => (props.disabled ? 'default' : 'pointer')};
  border-radius: 4px;
  padding: 0;
  opacity: ${(props) => (props.disabled ? 0.3 : 1)};
  user-select: none;
  -moz-user-select: none;
  -webkit-user-select: none;
  -ms-user-select: none;
  &:hover {
    background: ${(props) => (props.disabled ? 'transparent' : '#F0F0F0')};
  }
`;

const WeekContainer = styled('div')`
  display: flex;
  padding: 4px 16px;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 4px;
  background: ${(props) => props.isSelected && '#F8F8F8'};
   :hover {
      background: #F8F8F8;
    }
`;

const OpacityWrapper = styled('div')`
display: flex;
 transition: ${TransitionGlobal};
    :hover {
      opacity: 0.7;
    }
`;

const ChevronTrigger = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  margin: -4px;
  border-radius: 4px;
  cursor: pointer;
  transition: ${TransitionGlobal};
  :hover {
    opacity: 0.7;
  }
`;

const MenuWrapper = styled('div')`
width: 250px;
  .rc-menu {
    border-radius: 4px !important;
    box-shadow: 0px 4px 8px 0px #3031331A !important;
    border: 1px solid #EEEEEE !important;
    margin: 0px !important;
    min-width: 0px !important;

    padding-top: 8px;
    padding-bottom: 8px;
  }
  
  .rc-menu-item:hover {
    background-color: #F5F5F5 !important;
  }
  
`;

const IconContainer = styled('div')`
  display: flex;
  align-items: center;
  width: 100%;
  svg {
    margin-right: 8px;
  }
`;

const ProgramLibraryFooter = () => {
  const dispatch = useDispatch();
  const guardNavigation = usePreSaveNavigationGuard();
  const containerRef = useRef(null);
  const weeksRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const viewType = useSelector((state) => state.calendar.data.viewType);
  const [openWeek, setOpenWeek] = useState(null);
  const [selectedWeek, setSelectedWeek] = useState(1); /** default selected week */
  const isPrimaryAnimatedSideBarOpen = useSelector(
    (state) => state.calendar.data.isPrimaryAnimatedSideBarOpen,
  );
  const nonDateBasedProgramItems = useSelector(
    (state) => state.calendar.data.nonDateBasedProgramItems,
  );
  const selectedProgramDayNumber = useSelector(
    (state) => state.calendar.data.selectedProgramDayNumber,
  );
  const [selectedDay, setSelectedDay] = useState(0);
  const selectedReduxNonDateBasedProgram = useSelector(
    (state) => state.calendar.data.selectedReduxNonDateBasedProgram,
  );
  const programType = useSelector((state) => state.calendar.data.programType);
  const groupByFilter = useSelector(
    (state) => state.calendar.data.groupByFilter,
  );

  const checkOverflow = useCallback(() => {
    const el = weeksRef.current;
    if (!el) return;
    setShowLeftArrow(el.scrollLeft > 0);
    setShowRightArrow(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  useEffect(() => {
    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [checkOverflow]);

  useEffect(() => {
    checkOverflow();
  }, [isPrimaryAnimatedSideBarOpen, nonDateBasedProgramItems, groupByFilter, checkOverflow]);

  const stopScroll = useCallback(() => {
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
  }, []);

  const startScroll = useCallback((direction) => {
    stopScroll();
    const step = direction === 'left' ? -4 : 4;
    scrollIntervalRef.current = setInterval(() => {
      const el = weeksRef.current;
      if (!el) return;
      el.scrollLeft += step;
      checkOverflow();
    }, 30);
  }, [stopScroll, checkOverflow]);

  useEffect(() => () => stopScroll(), [stopScroll]);

  const groupedDays = [];

  Object.values(nonDateBasedProgramItems || {}).forEach((item) => {
    const existingDayInWeek = groupedDays.find((d) => d.dayInWeek === item.dayInWeek);

    if (existingDayInWeek) {
      existingDayInWeek.weeks.push(item);
    } else {
      groupedDays.push({
        dayInWeek: item.dayInWeek,
        dayNumber: item.dayNumber,
        weekNumber: item.weekNumber,
        weeks: [item],
      });
    }
  });

  const sortedGroupedDays = groupedDays.map((day) => ({
    ...day,
    weeks: [...day.weeks].sort((a, b) => a.weekNumber - b.weekNumber),
  }));

  /** Step 1: build a grouped array manually using forEach + find */
  const groupedWeeks = [];

  /** Convert the dictionary into an array of values first */
  Object.values(nonDateBasedProgramItems || {}).forEach((item) => {
    const existingWeek = groupedWeeks.find((w) => w.weekNumber === item.weekNumber);

    if (existingWeek) {
      existingWeek.days.push(item);
    } else {
      groupedWeeks.push({
        weekNumber: item.weekNumber,
        days: [item],
      });
    }
  });

  /** Step 2: sort days in each week */
  const sortedGroupedWeeks = groupedWeeks.map((week) => ({
    ...week,
    days: [...week.days].sort((a, b) => a.dayInWeek - b.dayInWeek),
  }));

  /** Step 3: sync selected week when day changes */
  useEffect(() => {
  /** Convert dict → array for searching */
    if (groupByFilter === 'Day') {
      setSelectedDay(selectedProgramDayNumber);
    }

    if (groupByFilter === 'Week') {
      const allDays = Object.values(nonDateBasedProgramItems || {});

      const dayThatIncludesWeekNumber = allDays.find(
        (day) => day.dayNumber === selectedProgramDayNumber,
      );

      const weekNumber = dayThatIncludesWeekNumber?.weekNumber || 1;
      setSelectedWeek(weekNumber);
    }
  }, [selectedProgramDayNumber, nonDateBasedProgramItems, groupByFilter]);

  /** Close dropdown on outside click or window scroll */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!containerRef.current.contains(e.target)) {
        setOpenWeek(null);
      }
    };

    const handleScroll = () => {
      setOpenWeek(null);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleMenuItemClick = (key, e, closeDropdown) => {
    if (!closeDropdown) {
      e.domEvent.stopPropagation();
    }
  };

  const addWeek = () => {
    const lastWeek = sortedGroupedWeeks[sortedGroupedWeeks?.length - 1];
    /** prevent user from adding more than 52 weeks */
    if (lastWeek.weekNumber >= 52) return;

    /** 1 Create the new day */
    const newDay = {
      dayInWeek: 1,
      dayNumber: lastWeek.weekNumber * 7, // last day in the current week
      exercises: [],
      repCount: 0,
      setCount: 0,
      weekNumber: lastWeek.weekNumber + 1,
      isLocalItem: true,
    };

    /** 2 Copy existing dictionary */
    const updatedItems = { ...nonDateBasedProgramItems };

    /** 3 Add the new day keyed by dayNumber */
    updatedItems[newDay.dayNumber] = newDay;

    /** 4 Update Redux */
    dispatch(setNonDateBasedProgramDays(updatedItems));
  };

  const addDay = () => {
    const lastDay = sortedGroupedDays[sortedGroupedDays?.length - 1];

    const newDay = {
      dayInWeek: lastDay.dayInWeek + 1,
      dayNumber: lastDay.dayNumber + 1,
      exercises: [],
      repCount: 0,
      setCount: 0,
      weekNumber: lastDay.weekNumber,
      isLocalItem: true,
    };

    /** 3 Copy existing dictionary */
    const updatedItems = { ...nonDateBasedProgramItems };

    /** 4 Add the new day keyed by dayNumber */
    updatedItems[newDay.dayNumber] = newDay;

    /** 5 Update Redux */
    dispatch(setNonDateBasedProgramDays(updatedItems));
  };

  /** Update the menuItems array to include Collapse Exercises option */
  const menuItems = [
    {
      key: '1', icon: 'copy', label: 'Duplicate', shortcut: '⌘+E', closeDropdown: true,
    },
    {
      key: '2', icon: 'delete', label: 'Clear Week', color: 'red', closeDropdown: true,
    },
  ];

  const MenuItemHoverableAlt = ({ hoverableItem, onMenuItemClick }) => {
    const [isHovered, setIsHovered] = useState(false);
    return (
      <MenuItem
        key={hoverableItem.key}
        style={{
          paddingTop: 7, paddingBottom: 7, paddingLeft: 14, cursor: 'pointer', background: isHovered ? '#F5F5F5' : 'white',
        }}
        onClick={(e) => onMenuItemClick(hoverableItem.key, e, hoverableItem.closeDropdown)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <IconContainer>
          <IcomoonReact iconSet={iconSet} size={16} icon={hoverableItem.icon} color={hoverableItem.color || '#9E9E9E'} />
          <Text fontSize='16px'>{hoverableItem.label}</Text>
        </IconContainer>
      </MenuItem>
    );
  };

  MenuItemHoverableAlt.propTypes = {
    hoverableItem: PropTypes.shape({
      key: PropTypes.string.isRequired,
      icon: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      color: PropTypes.string,
      closeDropdown: PropTypes.bool,
    }).isRequired,
    onMenuItemClick: PropTypes.func.isRequired,
  };

  /** Update the renderMenuItems function to handle the new Collapse Exercises option */
  const renderMenuItems = (week) => menuItems.map((item) => {
    /** Handle the "save workout" menu item specifically */
    if (item.key === '1') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            const savedWorkoutId = selectedReduxNonDateBasedProgram?.id;
            const weekNumber = week?.weekNumber;
            dispatch(setActiveModal('confirmDeleteModal', {
              savedWorkoutId,
              weekNumber,
              deleteWeek: false,
              duplicateWeek: true,
            }));
            setOpenWeek(null);
          }}
        />
      );
    }
    /** Handle the "Clear Workout Items" menu item */
    if (item.key === '2') {
      return (
        <MenuItemHoverableAlt
          key={item.key}
          hoverableItem={item}
          onMenuItemClick={() => {
            if (programType === 'free-form-program') {
              /** Flatten all exercises from all days in this week */
              const exercises = week?.days?.flatMap((day) => day?.exercises || []) || [];
              /** Pre-save items are stamped with their source dayNumber so the
               * reducer can target the right day when removing them from Redux.
               */
              const preSaveItems = (week?.days || []).flatMap((day) => (
                (day?.exercises || [])
                  .filter((ex) => typeof ex?.assignedId === 'string')
                  .map((ex) => ({ ...ex, dayNumber: day.dayNumber }))
              ));
              const savedItems = exercises.filter(
                (exerciseItem) => typeof exerciseItem?.assignedId !== 'string',
              );
              if (exercises?.length) {
                dispatch(setActiveModal('confirmDeleteModal', {
                  programName: selectedReduxNonDateBasedProgram?.programName,
                  weekNumber: week.weekNumber,
                  isBulkDelete: true,
                  bulkDeleteAssignedIds: savedItems?.map((exercise) => exercise?.assignedId),
                  deleteWeek: true,
                  preSaveItems,
                }));
              } else {
                /** if there are no exercises and the user is
                 * clearing the week, they are trying to delete the week
                 * we can do this if the week is a trailing week that is empty
                 * we hit the fetch endpoint so that we can refresh and remove the trailing week
                 */
                // const refresh = true;
                // dispatch(fetchNonDateBasedProgramItemsByDayRange(
                //   accountCode,
                //   selectedReduxNonDateBasedProgram?.id,
                //   null,
                //   null,
                //   refresh,
                //   week.weekNumber,
                // ));
              }
            }
          }}
        />
      );
    }
    return (
      <MenuItemHoverableAlt
        key={item.key}
        hoverableItem={item}
        onMenuItemClick={handleMenuItemClick}
      />
    );
  });

  return (
    <Wrapper
      ref={containerRef}
      viewType={viewType}
      isPrimaryAnimatedSideBarOpen={isPrimaryAnimatedSideBarOpen}
    >
      {groupByFilter === 'Day' ? (
        <WeeksWrapper ref={weeksRef} onScroll={checkOverflow}>
          {sortedGroupedDays.map((day) => (
            <WeekContainer
              key={day.dayNumber}
              isSelected={selectedDay === day.dayNumber}
              onClick={() => {
                dispatch(setSelectedProgramDayNumber(day.dayNumber));
              }}
            >
              <Text fontSize='16px' fontWeight={selectedDay === day.dayNumber ? 700 : 400} color={selectedDay !== day.dayNumber && '#9E9E9E'}>
                Day
              </Text>
              <Text fontSize='16px' fontWeight={selectedDay === day.dayNumber ? 700 : 400} color={selectedDay !== day.dayNumber && '#9E9E9E'}>
                {day.dayInWeek}
              </Text>
              <IcomoonReact
                iconSet={iconSet}
                size={10}
                icon='down-arrow'
                color={selectedDay !== day.dayNumber ? '#9E9E9E' : '#444444'}
              />
            </WeekContainer>
          ))}
        </WeeksWrapper>
      ) : (
        <WeeksWrapper ref={weeksRef} onScroll={checkOverflow}>
          {sortedGroupedWeeks.map((week) => (
            <Dropdown
              trigger={[]}
              overlay={(
                <MenuWrapper>
                  <Menu mode='vertical'>{renderMenuItems(week)}</Menu>
                </MenuWrapper>
            )}
              visible={openWeek === week.weekNumber}
              onVisibleChange={(visible) => {
                setOpenWeek(visible ? week.weekNumber : null);
              }}
              placement='bottomLeft'
            >
              <WeekContainer
                key={week.weekNumber}
                isSelected={selectedWeek === week.weekNumber}
                onClick={() => {
                  const firstDayInClickedWeek = week.days.filter((day) => day.dayInWeek === 1);
                  const { dayNumber } = firstDayInClickedWeek[0];
                  if (selectedWeek === week.weekNumber) {
                    dispatch(setSelectedProgramDayNumber(dayNumber));
                    return;
                  }
                  guardNavigation(() => {
                    dispatch(setSelectedProgramDayNumber(dayNumber));
                  });
                }}
              >
                <Text fontSize='16px' fontWeight={selectedWeek === week.weekNumber ? 700 : 400} color={selectedWeek !== week.weekNumber && '#9E9E9E'}>
                  Week
                </Text>
                <Text fontSize='16px' fontWeight={selectedWeek === week.weekNumber ? 700 : 400} color={selectedWeek !== week.weekNumber && '#9E9E9E'}>
                  {week.weekNumber}
                </Text>
                <ChevronTrigger
                  onClick={(e) => {
                    if (viewType !== 'weekView') return;
                    e.stopPropagation();
                    setOpenWeek(openWeek === week.weekNumber ? null : week.weekNumber);
                  }}
                >
                  <IcomoonReact
                    iconSet={iconSet}
                    size={10}
                    icon='down-arrow'
                    color={selectedWeek !== week.weekNumber ? '#9E9E9E' : '#444444'}
                  />
                </ChevronTrigger>
              </WeekContainer>
            </Dropdown>
          ))}
        </WeeksWrapper>
      )}
      {(showLeftArrow || showRightArrow) && (
        <ArrowsWrapper>
          <ArrowButton
            disabled={!showLeftArrow}
            onMouseDown={() => showLeftArrow && startScroll('left')}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={() => showLeftArrow && startScroll('left')}
            onTouchEnd={stopScroll}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='left-arrow'
              color='#444444'
            />
          </ArrowButton>
          <ArrowButton
            disabled={!showRightArrow}
            onMouseDown={() => showRightArrow && startScroll('right')}
            onMouseUp={stopScroll}
            onMouseLeave={stopScroll}
            onTouchStart={() => showRightArrow && startScroll('right')}
            onTouchEnd={stopScroll}
          >
            <IcomoonReact
              iconSet={iconSet}
              size={12}
              icon='right-arrow'
              color='#444444'
            />
          </ArrowButton>
        </ArrowsWrapper>
      )}
      <OpacityWrapper>
        {groupByFilter === 'Day' ? (
          <Text
            fontSize='16px'
            color='#77A7FF'
            style={{
              cursor: sortedGroupedDays?.length < 7 ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              if (sortedGroupedDays?.length < 7) {
                addDay();
              }
            }}
          >
            + Add Day
          </Text>
        ) : (
          <Text
            fontSize='16px'
            color='#77A7FF'
            style={{
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
            onClick={() => {
              addWeek();
            }}
          >
            + Add Week
          </Text>
        )}
      </OpacityWrapper>
    </Wrapper>
  );
};

export default ProgramLibraryFooter;
